<template>
    <div>
      <Navbar />
  
      <main>
  
        <!-- ── Page hero ── -->
        <section class="page-hero">
          <div class="page-hero-overlay"></div>
          <div class="page-container">
            <div class="page-hero-content">
              <div class="hero-meta" v-if="details.category">
                <router-link :to="'/blog/category/' + details.category.slug" class="hero-category">
                  {{ details.category.name }}
                </router-link>
              </div>
              <h1 class="page-hero-title">{{ details.title }}</h1>
              <div class="hero-info">
                <span v-if="details.created_at" class="hero-date">
                  <font-awesome-icon icon="calendar-alt" class="me-1" /> {{ formatDate(details.created_at) }}
                </span>
                <span v-if="details.comments" class="hero-comments">
                  <font-awesome-icon icon="comment" class="me-1" /> {{ details.comments.length }} Comments
                </span>
              </div>
              <nav class="breadcrumb-nav" aria-label="Breadcrumb">
                <router-link to="/home" class="breadcrumb-link">Home</router-link>
                <span class="breadcrumb-sep">/</span>
                <router-link to="/blog" class="breadcrumb-link">Blog</router-link>
                <span class="breadcrumb-sep">/</span>
                <span class="breadcrumb-current">{{ details.title }}</span>
              </nav>
            </div>
          </div>
        </section>
  
        <!-- ── Body ── -->
        <section class="section-pad">
          <div class="page-container">
            <div class="blog-layout">
  
              <!-- ── Main content ── -->
              <div class="blog-main">
  
                <!-- Featured image -->
                <div v-if="details.image" class="post-image-wrap">
                  <img :src="url.hostName + 'images/blog/' + details.image" :alt="details.title" class="post-image" />
                </div>
  
                <!-- Video embed -->
                <div v-if="details.video_id" class="post-video-wrap">
                  <iframe
                    :src="'https://www.youtube.com/embed/' + details.video_id"
                    allowfullscreen
                    class="post-video"
                  ></iframe>
                </div>
  
                <!-- Post body -->
                <div class="post-body" v-html="details.description"></div>
  
                <!-- Tags + Share -->
                <div class="post-footer">
                  <div class="post-tags">
                    <span class="tags-label">Tag:</span>
                    <router-link v-if="details.category" :to="'/blog/category/' + details.category.slug" class="post-tag">
                      {{ details.category.name }}
                    </router-link>
                  </div>
  
                  <div class="share-wrap">
                    <span class="share-label">Share:</span>
                    <div class="share-icons">
                      <a
                        :href="'http://www.facebook.com/sharer.php?u=' + baseurl + '/blog/' + details.slug"
                        target="_blank" rel="nofollow noopener"
                        class="share-btn share-btn--facebook"
                        aria-label="Share on Facebook"
                        @click.prevent="openShare('http://www.facebook.com/sharer.php?u=' + baseurl + '/blog/' + details.slug)"
                      >
                        <i class="fab fa-facebook-f"></i>
                      </a>
                      <a
                        :href="'http://twitter.com/share?text=' + details.title + '&url=' + baseurl + '/blog/' + details.slug"
                        target="_blank" rel="nofollow noopener"
                        class="share-btn share-btn--twitter"
                        aria-label="Share on Twitter"
                        @click.prevent="openShare('http://twitter.com/share?text=' + details.title + '&url=' + baseurl + '/blog/' + details.slug)"
                      >
                        <i class="fab fa-twitter"></i>
                      </a>
                      <a
                        :href="'http://www.linkedin.com/shareArticle?mini=true&url=' + baseurl + '/blog/' + details.slug"
                        target="_blank" rel="nofollow noopener"
                        class="share-btn share-btn--linkedin"
                        aria-label="Share on LinkedIn"
                        @click.prevent="openShare('http://www.linkedin.com/shareArticle?mini=true&url=' + baseurl + '/blog/' + details.slug)"
                      >
                        <i class="fab fa-linkedin-in"></i>
                      </a>
                    </div>
                  </div>
                </div>
  
                <!-- ── Comments ── -->
                <div class="comments-section">
                  <h4 class="comments-title">
                    {{ details.comments?.length ?? 0 }} Comment{{ details.comments?.length !== 1 ? 's' : '' }}
                  </h4>
  
                  <div v-if="details.comments?.length" class="comments-list">
                    <div v-for="comment in details.comments" :key="comment.id" class="comment-item">
                      <div class="comment-avatar">
                        {{ userInitials(comment.user) }}
                      </div>
                      <div class="comment-content-wrap">
                        <div class="comment-header">
                          <span class="comment-author">{{ comment.user ? comment.user.first_name + ' ' + comment.user.last_name : 'Anonymous' }}</span>
                          <span class="comment-date">{{ formatDate(comment.created_at) }} at {{ formatTime(comment.created_at) }}</span>
                        </div>
                        <p class="comment-text">{{ comment.comment }}</p>
                        <button v-if="isLogin" class="reply-btn" @click="openReply(comment.id)">
                          <font-awesome-icon icon="reply" class="me-1" /> Reply
                        </button>
  
                        <!-- Sub-comments -->
                        <div v-if="comment.sub?.length" class="sub-comments">
                          <div v-for="sub in comment.sub" :key="sub.id" class="comment-item comment-item--sub">
                            <div class="comment-avatar comment-avatar--sm">
                              {{ userInitials(sub.user) }}
                            </div>
                            <div class="comment-content-wrap">
                              <div class="comment-header">
                                <span class="comment-author">{{ sub.user ? sub.user.first_name + ' ' + sub.user.last_name : 'Anonymous' }}</span>
                                <span class="comment-date">{{ formatDate(sub.created_at) }} at {{ formatTime(sub.created_at) }}</span>
                              </div>
                              <p class="comment-text">{{ sub.comment }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
  
                  <!-- Empty comments -->
                  <div v-else class="comments-empty">
                    <font-awesome-icon icon="comment-slash" class="comments-empty-icon" />
                    <p>No comments yet. Be the first to leave one!</p>
                  </div>
                </div>
  
                <!-- ── Comment / Login form ── -->
                <div class="comment-form-card">
                  <template v-if="isLogin">
                    <h4 class="form-card-title">Leave a Comment</h4>
  
                    <div v-if="validationErrors.length" class="form-error-box">
                      <div v-for="(err, i) in validationErrors" :key="i">{{ err }}</div>
                    </div>
  
                    <div class="commenter-info">
                      <div class="commenter-avatar">{{ userInitials(user) }}</div>
                      <div>
                        <div class="commenter-name">{{ user.first_name }} {{ user.last_name }}</div>
                        <div class="commenter-email">{{ user.email }}</div>
                      </div>
                    </div>
  
                    <form @submit.prevent="submitComment">
                      <div class="form-group-pub">
                        <label class="form-label-pub">Your Comment</label>
                        <textarea
                          v-model="comment"
                          class="form-input-pub"
                          rows="5"
                          placeholder="Share your thoughts..."
                          required
                        ></textarea>
                      </div>
                      <button type="submit" class="btn-primary-md" :disabled="loading || !comment.trim()">
                        <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                        {{ loading ? 'Submitting...' : 'Post Comment' }}
                      </button>
                    </form>
                  </template>
  
                  <template v-else>
                    <h4 class="form-card-title">Sign In to Comment</h4>
                    <p class="form-card-sub">You need to be logged in to leave a comment.</p>
  
                    <div v-if="validationErrors.length" class="form-error-box">
                      <div v-for="(err, i) in validationErrors" :key="i">{{ err }}</div>
                    </div>
  
                    <form @submit.prevent="loginUser">
                      <div class="login-form-row">
                        <div class="form-group-pub">
                          <label class="form-label-pub">Email Address</label>
                          <input type="email" v-model="email" class="form-input-pub" placeholder="you@example.com" required />
                        </div>
                        <div class="form-group-pub">
                          <label class="form-label-pub">Password</label>
                          <input type="password" v-model="password" class="form-input-pub" placeholder="••••••••" required />
                        </div>
                      </div>
                      <button type="submit" class="btn-primary-md" :disabled="loading">
                        <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                        {{ loading ? 'Signing in...' : 'Sign In' }}
                      </button>
                    </form>
                  </template>
                </div>
  
              </div>
  
              <!-- ── Sidebar ── -->
              <aside class="blog-sidebar">
  
                <!-- Search -->
                <div class="sidebar-widget">
                  <h5 class="widget-title">Search</h5>
                  <form class="sidebar-search" @submit.prevent="$router.push('/search/blog?search=' + searchQuery)">
                    <input type="text" v-model="searchQuery" class="sidebar-search-input" placeholder="Search articles..." />
                    <button type="submit" class="sidebar-search-btn">
                      <font-awesome-icon icon="search" />
                    </button>
                  </form>
                </div>
  
                <!-- Categories -->
                <div v-if="categories.length" class="sidebar-widget">
                  <h5 class="widget-title">Categories</h5>
                  <div class="widget-list">
                    <router-link
                      v-for="cat in categories"
                      :key="cat.id"
                      :to="'/blog/category/' + cat.slug"
                      class="widget-list-item"
                    >
                      <span>{{ cat.name }}</span>
                      <font-awesome-icon icon="angle-right" class="widget-list-arrow" />
                    </router-link>
                  </div>
                </div>
  
                <!-- Recent Posts -->
                <div v-if="latest?.length" class="sidebar-widget">
                  <h5 class="widget-title">Recent Posts</h5>
                  <div class="widget-posts">
                    <router-link v-for="post in latest" :key="post.id" :to="'/blog/' + post.slug" class="widget-post">
                      <div class="widget-post-thumb">
                        <img :src="url.hostName + 'images/blog/' + post.image" :alt="post.title" />
                      </div>
                      <div class="widget-post-info">
                        <p class="widget-post-title">{{ post.title }}</p>
                      </div>
                    </router-link>
                  </div>
                </div>
  
                <!-- Featured Posts -->
                <div v-if="featured?.length" class="sidebar-widget">
                  <h5 class="widget-title">Featured Posts</h5>
                  <div class="widget-posts">
                    <router-link v-for="post in featured" :key="post.id" :to="'/blog/' + post.slug" class="widget-post">
                      <div class="widget-post-thumb">
                        <img :src="url.hostName + 'images/blog/' + post.image" :alt="post.title" />
                      </div>
                      <div class="widget-post-info">
                        <p class="widget-post-title">{{ post.title }}</p>
                      </div>
                    </router-link>
                  </div>
                </div>
  
              </aside>
            </div>
          </div>
        </section>
  
      </main>
  
      <Footer />
  
      <!-- ── Reply Modal ── -->
      <div class="pub-modal-overlay" :class="{ open: replyModalOpen }" @click.self="closeReply">
        <div class="pub-modal-box">
          <div class="pub-modal-header">
            <h5 class="pub-modal-title">Leave a Reply</h5>
            <button class="pub-modal-close" @click="closeReply">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="pub-modal-body">
            <div v-if="validationErrors.length" class="form-error-box">
              <div v-for="(err, i) in validationErrors" :key="i">{{ err }}</div>
            </div>
            <div class="form-group-pub" style="margin-bottom:0;">
              <label class="form-label-pub">Your Reply</label>
              <textarea
                v-model="replycomment"
                class="form-input-pub"
                rows="4"
                placeholder="Write your reply..."
                required
              ></textarea>
            </div>
          </div>
          <div class="pub-modal-footer">
            <button class="pub-btn-ghost" @click="closeReply">Cancel</button>
            <button class="pub-btn-primary" :disabled="loading || !replycomment.trim()" @click="submitReplyComment">
              <font-awesome-icon v-if="loading" icon="spinner" spin />
              {{ loading ? 'Posting...' : 'Post Reply' }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="replyModalOpen" class="pub-backdrop"></div>
  
    </div>
  </template>
  
  <script>
  import Navbar  from '../components/Navbar';
  import Footer  from '../components/footer';
  import axios   from 'axios';
  import moment  from 'moment';
  import hostName from '../main';
  import { useRoute, useRouter } from 'vue-router';
  import { useToast } from 'vue-toastification';
  
  export default {
    name: 'BlogDetail',
  
    components: { Navbar, Footer },
  
    data() {
      return {
        details:         {},
        categories:      [],
        featured:        [],
        latest:          [],
        loading:         false,
        url:             hostName,
        route:           useRoute(),
        router:          useRouter(),
        toast:           useToast(),
        baseurl:         window.location.origin,
        isLogin:         !!localStorage.getItem('token'),
        user:            {},
        comment:         '',
        replycomment:    '',
        parent_id:       '',
        replyModalOpen:  false,
        searchQuery:     '',
        email:           '',
        password:        '',
        validationErrors: [],
      };
    },
  
    methods: {
      // ── Helpers ──────────────────────────────────────────────────────
      formatDate(date)  { return moment(date).format('MMM DD, YYYY'); },
      formatTime(date)  { return moment(date).format('h:mm a'); },
  
      userInitials(user) {
        if (!user) return '?';
        return ((user.first_name?.[0] ?? '') + (user.last_name?.[0] ?? '')).toUpperCase();
      },
  
      openShare(url) {
        window.open(url, '_blank', 'width=600,height=600,top=200,left=200');
      },
  
      // ── Reply modal ──────────────────────────────────────────────────
      openReply(id) {
        this.parent_id      = id;
        this.replycomment   = '';
        this.replyModalOpen = true;
      },
      closeReply() {
        this.parent_id      = '';
        this.replyModalOpen = false;
      },
  
      // ── Data ─────────────────────────────────────────────────────────
      async pageData() {
        try {
          const { data } = await axios.get('/admin/blog-detail/' + this.route.params.id);
          this.details    = data.data.data;
          this.categories = data.data.category;
          this.featured   = data.data.featured;
          this.latest     = data.data.latest;
        } catch (err) {
            console.log(err?.response?.data?.message);
        }
      },
  
      getCurrentUser() {
        if (!this.isLogin) return;
        axios.get('/user')
          .then(({ data }) => { this.user = data.data.data; })
          .catch(() => {});
      },
  
      // ── Comment ──────────────────────────────────────────────────────
      async submitComment() {
        if (!this.comment.trim()) return;
        this.loading = true;
        try {
          const { data } = await axios.post('/admin/blog-comment', { comment: this.comment, blog_id: this.details.id, user_id: this.user.id });
          if (data.status === 200) {
            this.toast.success(data.message);
            this.comment = '';
            this.pageData();
          }
        } catch (err) {
          const status = err.response?.status;
          if (status === 400 || status === 422) {
            this.validationErrors = err.response.data.message;
            this.toast.error('There are errors in your input.');
          } else {
            this.toast.error('Could not post your comment. Please try again.');
          }
        } finally {
          this.loading = false;
        }
      },
  
      // ── Reply ────────────────────────────────────────────────────────
      async submitReplyComment() {
        if (!this.replycomment.trim()) return;
        this.loading = true;
        try {
          const { data } = await axios.post('/admin/blog-comment', { comment: this.replycomment, blog_id: this.details.id, user_id: this.user.id, parent_id: this.parent_id });
          if (data.status === 200) {
            this.toast.success(data.message);
            this.closeReply();
            this.pageData();
          }
        } catch (err) {
          const status = err.response?.status;
          if (status === 400 || status === 422) {
            this.validationErrors = err.response.data.message;
            this.toast.error('There are errors in your input.');
          } else {
            this.toast.error('Could not post your reply. Please try again.');
          }
        } finally {
          this.loading = false;
        }
      },
  
      // ── Login ────────────────────────────────────────────────────────
      async loginUser() {
        if (!this.email || !this.password) return;
        this.loading = true;
        try {
          const { data } = await axios.post('/login-web', { email: this.email, password: this.password });
          localStorage.setItem('token', data.data.original.accessToken);
          axios.defaults.headers['Authorization'] = `Bearer ${data.data.original.accessToken}`;
          this.toast.success('Login successful.');
          window.location.reload();
        } catch (err) {
          const status = err.response?.status;
          if (status === 400) { this.validationErrors = err.response.data.message; }
          else if (status === 402) { this.router.push('send-email-verifcation'); this.toast.error(err.response.data.message); }
          else { this.toast.error(err.response?.data?.message ?? 'Login failed. Please try again.'); }
        } finally {
          this.loading = false;
        }
      },
    },
  
    mounted() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.pageData();
      this.getCurrentUser();
    },
  };
  </script>
  
  <style scoped>
  /* ── Shared ──────────────────────────────────────────────────────────── */
  .page-container { max-width:1160px; margin:0 auto; padding:0 24px; }
  .section-pad    { padding:64px 0; }
  .me-1           { margin-right:4px; }
  .me-2           { margin-right:8px; }
  
  /* ── Page hero ───────────────────────────────────────────────────────── */
  .page-hero {
    position: relative;
    background: linear-gradient(135deg, #051B35 0%, #004AAD 100%);
    padding: 80px 0 56px;
    overflow: hidden;
  }
  .page-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('/static/img/bg-1.jpg') center/cover no-repeat;
    opacity: 0.06;
  }
  .page-hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(5,27,53,.94) 0%, rgba(0,74,173,.72) 100%);
  }
  .page-hero-content { position:relative; z-index:1; }
  .hero-meta          { margin-bottom:12px; }
  .hero-category      { display:inline-block; background:rgba(255,255,255,.12); color:rgba(255,255,255,.9); border:1px solid rgba(255,255,255,.2); border-radius:20px; padding:4px 14px; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; text-decoration:none; }
  .hero-category:hover { background:rgba(255,255,255,.2); }
  .page-hero-title { font-size:clamp(24px,4vw,42px); font-weight:800; color:#fff; margin:14px 0 16px; line-height:1.25; }
  .hero-info       { display:flex; align-items:center; gap:20px; margin-bottom:18px; flex-wrap:wrap; }
  .hero-date, .hero-comments { font-size:13px; color:rgba(255,255,255,.7); display:flex; align-items:center; gap:4px; }
  
  /* ── Breadcrumb ──────────────────────────────────────────────────────── */
  .breadcrumb-nav     { display:inline-flex; align-items:center; gap:8px; font-size:13px; flex-wrap:wrap; }
  .breadcrumb-link    { color:rgba(255,255,255,.65); text-decoration:none; transition:color .15s; }
  .breadcrumb-link:hover { color:#fff; }
  .breadcrumb-sep     { color:rgba(255,255,255,.3); }
  .breadcrumb-current { color:rgba(255,255,255,.45); }
  
  /* ── Layout ──────────────────────────────────────────────────────────── */
  .blog-layout { display:grid; grid-template-columns:1fr 320px; gap:40px; align-items:start; }
  .blog-main   { min-width:0; }
  
  /* ── Post ────────────────────────────────────────────────────────────── */
  .post-image-wrap { border-radius:12px; overflow:hidden; margin-bottom:32px; }
  .post-image      { width:100%; display:block; object-fit:cover; }
  .post-video-wrap { border-radius:12px; overflow:hidden; margin-bottom:32px; aspect-ratio:16/9; }
  .post-video      { width:100%; height:100%; border:none; }
  
  .post-body { font-size:15.5px; line-height:1.8; color:#374151; margin-bottom:32px; }
  .post-body h1,.post-body h2,.post-body h3 { color:#1a2b45; font-weight:700; margin:28px 0 12px; }
  .post-body p  { margin-bottom:16px; }
  .post-body img { max-width:100%; border-radius:8px; margin:8px 0; }
  .post-body a  { color:#004AAD; }
  
  /* ── Post footer ─────────────────────────────────────────────────────── */
  .post-footer { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; padding:20px 0; border-top:1px solid #f0f4f8; border-bottom:1px solid #f0f4f8; margin-bottom:40px; }
  .post-tags   { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .tags-label  { font-size:13px; font-weight:600; color:#7a90b0; }
  .post-tag    { background:rgba(0,74,173,.08); color:#004AAD; border:1px solid rgba(0,74,173,.18); border-radius:20px; padding:3px 12px; font-size:12px; font-weight:600; text-decoration:none; }
  .post-tag:hover { background:rgba(0,74,173,.14); }
  .share-wrap  { display:flex; align-items:center; gap:10px; }
  .share-label { font-size:13px; font-weight:600; color:#7a90b0; }
  .share-icons { display:flex; gap:6px; }
  .share-btn   { width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:13px; color:#fff; text-decoration:none; transition:opacity .15s; }
  .share-btn:hover { opacity:.85; }
  .share-btn--facebook { background:#1877f2; }
  .share-btn--twitter  { background:#1da1f2; }
  .share-btn--linkedin { background:#0077b5; }
  
  /* ── Comments ────────────────────────────────────────────────────────── */
  .comments-section { margin-bottom:40px; }
  .comments-title   { font-size:18px; font-weight:700; color:#1a2b45; margin:0 0 24px; padding-bottom:14px; border-bottom:1px solid #f0f4f8; }
  .comments-list    { display:flex; flex-direction:column; gap:24px; }
  .comment-item     { display:flex; gap:14px; }
  .comment-item--sub { padding-left:0; }
  .comment-avatar   { width:38px; height:38px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .comment-avatar--sm { width:30px; height:30px; font-size:11px; }
  .comment-content-wrap { flex:1; min-width:0; }
  .comment-header   { display:flex; align-items:center; gap:10px; margin-bottom:6px; flex-wrap:wrap; }
  .comment-author   { font-size:14px; font-weight:700; color:#1a2b45; }
  .comment-date     { font-size:12px; color:#7a90b0; }
  .comment-text     { font-size:14px; color:#4a5568; line-height:1.6; margin:0 0 8px; }
  .sub-comments     { margin-top:16px; padding-top:16px; border-top:1px solid #f4f7fb; display:flex; flex-direction:column; gap:16px; }
  .reply-btn        { display:inline-flex; align-items:center; gap:5px; background:transparent; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:12px; font-weight:600; color:#7a90b0; cursor:pointer; transition:all .15s; }
  .reply-btn:hover  { background:#f4f7fb; color:#004AAD; border-color:#c5d6f5; }
  .comments-empty   { display:flex; flex-direction:column; align-items:center; padding:40px 0; color:#7a90b0; text-align:center; }
  .comments-empty-icon { font-size:32px; margin-bottom:12px; opacity:.3; }
  
  /* ── Comment form card ───────────────────────────────────────────────── */
  .comment-form-card { background:#f8fafc; border:1px solid #e4eaf2; border-radius:14px; padding:28px; }
  .form-card-title   { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .form-card-sub     { font-size:13px; color:#7a90b0; margin:0 0 20px; }
  .form-error-box    { background:#fff5f5; border-left:3px solid #e03535; border-radius:0 6px 6px 0; padding:10px 14px; margin-bottom:16px; font-size:13px; color:#c02828; }
  .commenter-info    { display:flex; align-items:center; gap:12px; margin-bottom:20px; }
  .commenter-avatar  { width:38px; height:38px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .commenter-name    { font-size:14px; font-weight:700; color:#1a2b45; }
  .commenter-email   { font-size:12px; color:#7a90b0; }
  .login-form-row    { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  
  .form-group-pub   { display:flex; flex-direction:column; gap:6px; margin-bottom:16px; }
  .form-label-pub   { font-size:13px; font-weight:600; color:#1a2b45; }
  .form-input-pub   { width:100%; padding:10px 14px; border:1px solid #dde5f0; border-radius:8px; font-size:14px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-pub:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  textarea.form-input-pub { resize:vertical; }
  
  .btn-primary-md { display:inline-flex; align-items:center; gap:8px; background:#004AAD; color:#fff; font-size:14px; font-weight:600; padding:10px 22px; border-radius:9px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-md:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-md:disabled { opacity:.55; cursor:not-allowed; }
  
  /* ── Sidebar ─────────────────────────────────────────────────────────── */
  .blog-sidebar { display:flex; flex-direction:column; gap:24px; }
  .sidebar-widget { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  .widget-title { font-size:14px; font-weight:700; color:#1a2b45; padding:16px 20px; border-bottom:1px solid #f0f4f8; margin:0; text-transform:uppercase; letter-spacing:.06em; }
  
  .sidebar-search { display:flex; gap:0; overflow:hidden; margin:12px; border:1px solid #dde5f0; border-radius:8px; }
  .sidebar-search-input { flex:1; padding:9px 12px; border:none; outline:none; font-size:13px; color:#1a2b45; }
  .sidebar-search-btn { padding:9px 14px; background:#004AAD; border:none; color:#fff; font-size:13px; cursor:pointer; transition:background .15s; }
  .sidebar-search-btn:hover { background:#003b8e; }
  
  .widget-list { display:flex; flex-direction:column; }
  .widget-list-item { display:flex; align-items:center; justify-content:space-between; padding:11px 20px; font-size:13.5px; color:#4a5568; text-decoration:none; border-bottom:1px solid #f4f7fb; transition:background .12s, color .12s; }
  .widget-list-item:last-child { border-bottom:none; }
  .widget-list-item:hover { background:#f4f7fb; color:#004AAD; }
  .widget-list-arrow { font-size:11px; color:#c0cce0; }
  
  .widget-posts { display:flex; flex-direction:column; }
  .widget-post  { display:flex; align-items:center; gap:12px; padding:12px 16px; text-decoration:none; border-bottom:1px solid #f4f7fb; transition:background .12s; }
  .widget-post:last-child { border-bottom:none; }
  .widget-post:hover { background:#f4f7fb; }
  .widget-post-thumb { width:48px; height:48px; border-radius:7px; overflow:hidden; flex-shrink:0; background:#f0f4f8; }
  .widget-post-thumb img { width:100%; height:100%; object-fit:cover; }
  .widget-post-title { font-size:13px; color:#1a2b45; margin:0; line-height:1.4; font-weight:500; }
  
  /* ── Reply Modal ─────────────────────────────────────────────────────── */
  .pub-modal-overlay  { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .pub-modal-overlay.open { opacity:1; pointer-events:all; }
  .pub-backdrop       { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.5); backdrop-filter:blur(3px); }
  .pub-modal-box      { background:#fff; border-radius:14px; width:100%; max-width:480px; margin:16px; box-shadow:0 24px 64px rgba(0,0,0,.16); overflow:hidden; transform:translateY(18px); transition:transform .25s ease; z-index:1051; position:relative; }
  .pub-modal-overlay.open .pub-modal-box { transform:translateY(0); }
  .pub-modal-header   { padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; display:flex; align-items:center; justify-content:space-between; }
  .pub-modal-title    { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
  .pub-modal-close    { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; }
  .pub-modal-close:hover { background:#f4f7fb; }
  .pub-modal-body     { padding:20px 24px; }
  .pub-modal-footer   { padding:16px 24px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }
  .pub-btn-ghost   { background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; }
  .pub-btn-ghost:hover { background:#e4eaf2; }
  .pub-btn-primary { background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; display:inline-flex; align-items:center; gap:6px; }
  .pub-btn-primary:hover:not(:disabled) { background:#003b8e; }
  .pub-btn-primary:disabled { opacity:.5; cursor:not-allowed; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:900px) {
    .blog-layout    { grid-template-columns:1fr; }
    .blog-sidebar   { order:-1; }
  }
  @media (max-width:600px) {
    .login-form-row { grid-template-columns:1fr; }
    .post-footer    { flex-direction:column; align-items:flex-start; }
    .section-pad    { padding:48px 0; }
  }
  </style>